import { lazy, Suspense, useEffect, useMemo, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useLang } from './i18n'
import { useTheme } from './theme'
import { api, bidDisplayLabel } from './lib/api'
import { useAgentAvailable } from './lib/agent-availability'
import { useReportAccount } from './lib/desktop'
import { useAgentWarmup } from './lib/useAgentWarmup'
import { onDesktopNavigate } from './lib/desktop-wake'
import { useBidProfile } from './lib/bid-profile'
import { useTemplateLibrary } from './lib/template-library'
import { resolveSession } from './lib/session'
import { usePath, parseRoute, navigate, homePath, bidPath, roomPath, knowledgePath, dashboardPath, settingsPath, TEMPLATES_SEGMENT } from './lib/router'
import { useOutline, roomTitles, partKindOf, partOf, artefactTypeOfRoom } from './lib/outline'
import { useBetaTester } from './lib/beta'
import { useConnected } from './lib/connected'
import { TopBar } from './components/TopBar'
import { ConnectedRoster } from './components/ConnectedRoster'
import { LeftNav } from './components/LeftNav'
import { Canvas } from './components/Canvas'
import { ChatPanel } from './components/ChatPanel'
import { KnowledgeView } from './components/KnowledgeView'
import { SourceInspector } from './components/SourceInspector'
import { HomePage } from './components/HomePage'
import { BidHome } from './components/BidHome'
import { BidSettingsPage } from './components/BidSettingsPage'
import { AccountSettingsPage } from './components/AccountSettingsPage'
import { CorporateTemplatesPage } from './components/CorporateTemplatesPage'
import { VersionRetentionPage } from './components/VersionRetentionPage'
import { FeedbackTriagePage, useTourRecording } from './userhub'
import { ReleaseNotes } from './components/ReleaseNotes'
import { BidUsage } from './components/BidUsage'
import { Footer } from './components/Footer'
import { DashboardView } from './components/DashboardView'
import { TourProvider, inTourWindow } from './tours/TourRunner'
import { useTourState } from './tours/useTourState'
import { WORKSPACE_STATE, type WorkspaceTourState } from './tours/probes'
import { GuidedToursLayer } from './components/GuidedToursWindow'

export type View = 'home' | 'editor' | 'knowledge' | 'releases' | 'dashboard' | 'settings' | 'account-settings' | 'corporate-templates' | 'version-retention' | 'feedback-triage' | 'template'

// The template view (AB#454) is its own chunk, like the slides editor: it draws with the slide
// renderer, which never weighs on the entry chunk.
const TemplateView = lazy(() => import('./components/template/TemplateView'))

/** Browser-tab title on the landing page, before a bid is chosen — mirrors index.html. */
export const defaultTitle = 'Niobe — agentic solutioning'

/**
 * The guided-tour engine wraps every shell (home, settings, admin pages, the workspace), so a tour
 * can lead from the home page into a bid (docs/spec/62-help.md). It sits inside UserhubShell
 * (main.tsx): a completed tour is recorded in userhub, and the congratulations card's 👍 / 👎 go
 * there too — fire-and-forget, so a down or slow userhub never blocks, delays or breaks a tour.
 */
export function App() {
  const { onEvent, verdicts } = useTourRecording()
  const [toursWindowOpen, setToursWindowOpen] = useState(false)
  return (
    <TourProvider onEvent={onEvent} verdicts={verdicts}>
      <AppShell onOpenGuidedTours={() => setToursWindowOpen(true)} />
      <GuidedToursLayer windowOpen={toursWindowOpen} onCloseWindow={() => setToursWindowOpen(false)} />
    </TourProvider>
  )
}

function AppShell({ onOpenGuidedTours }: { onOpenGuidedTours: () => void }) {
  const { t, lang } = useLang()
  const { resolvedTheme, textScale } = useTheme()
  const session = useMemo(() => resolveSession(), [])
  // Single capability gate for the whole local-AI surface (chat panel, dashboard/workflow,
  // chat reopen/resize). True inside Niobe Desktop, off in a browser — see
  // lib/agent-availability.ts for why the browser case is a hint rather than a gate.
  const agentAvailable = useAgentAvailable()
  // Let the desktop tray show who is signed in; a no-op in a browser.
  useReportAccount(session.user.name)
  // Keep the local Claude sign-in alive across sleeps: on mount, on refocus and on the desktop
  // shell's wake signal, ask the passerelle to refresh the token (or surface that it is dead)
  // before the member types. Gated to Desktop inside the hook, and gated again — hard — by the
  // passerelle, which runs at most one warm-up per 30 minutes and only when the token has actually
  // expired. See lib/useAgentWarmup.ts.
  useAgentWarmup()
  const [navHidden, setNavHidden] = useState(false)
  // Focus mode (AB#282): the open part's editor gets the whole height and the nav's width. It only
  // overrides what is DRAWN — navHidden, navWidth and every stored pref stay as they were, so
  // leaving it restores the layout exactly. Never persisted: a reload opens in normal mode.
  const [focusRequested, setFocusRequested] = useState(false)
  const [chatOpen, setChatOpen] = useState(true)
  // Width of the left nav and the right panel (chat / inspector), drag-resizable and persisted.
  const [navWidth, setNavWidth] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('niobe.navW'))
      return v >= 220 && v <= 520 ? v : 316
    } catch {
      return 316
    }
  })
  const [chatWidth, setChatWidth] = useState<number>(() => {
    try {
      const v = Number(localStorage.getItem('niobe.chatW'))
      return v >= 300 && v <= 760 ? v : 384
    } catch {
      return 384
    }
  })
  const [resizing, setResizing] = useState(false)
  useEffect(() => {
    try {
      localStorage.setItem('niobe.navW', String(navWidth))
      localStorage.setItem('niobe.chatW', String(chatWidth))
    } catch {
      /* storage unavailable */
    }
  }, [navWidth, chatWidth])

  const startNavResize = (e: ReactMouseEvent): void => {
    e.preventDefault()
    setResizing(true)
    const onMove = (ev: MouseEvent): void => {
      const max = Math.min(520, Math.round(window.innerWidth * 0.4))
      setNavWidth(Math.min(max, Math.max(220, ev.clientX)))
    }
    const onUp = (): void => {
      setResizing(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  const startChatResize = (e: ReactMouseEvent): void => {
    e.preventDefault()
    setResizing(true)
    const onMove = (ev: MouseEvent): void => {
      const max = Math.min(760, Math.round(window.innerWidth * 0.55))
      setChatWidth(Math.min(max, Math.max(300, window.innerWidth - ev.clientX)))
    }
    const onUp = (): void => {
      setResizing(false)
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // The URL is the source of truth: home (/), a bid (/bid/:id) or a part (/bid/:id/:deliv/:part).
  const path = usePath()
  const route = parseRoute(path)
  const { bid, section: settingsSection } = route
  // `/bid/:bid/templates/:id` is also the shape of a part of an artefact whose id is `templates`:
  // while the bid's outline holds that part, it is the part that opens, never the template view.
  const { artefacts } = useOutline(bid)
  const shadowRoom = route.view === 'template' && bid && route.template ? [bid, TEMPLATES_SEGMENT, route.template].join('/') : null
  const shadowed = shadowRoom !== null && partOf(artefacts, shadowRoom) !== null
  const rawView: View = shadowed ? 'editor' : route.view
  const room = shadowed ? shadowRoom : route.room
  const templateId = rawView === 'template' ? (route.template ?? null) : null
  // A viewer of the ACTIVE bid gets a read-only editor and an explicit banner, rather than an
  // editor that silently swallows their keystrokes. The server refuses their writes regardless.
  const { isViewer, isAdmin } = useBidProfile(bid)
  // The dashboard/workflow view is part of the AI surface (its only action is generateNode), so
  // it is hidden in MVP 1: treat it as the bid overview and redirect the URL back to the bid.
  //
  // A VIEWER is excluded too, on the second axis: generateNode writes parts, and the backend's
  // write gate refuses them at POST /v1/revisions. Availability and permission are orthogonal —
  // inside Niobe Desktop `agentAvailable` is true for everyone, so without `!isViewer` a viewer
  // would get a dashboard whose every button 403s.
  const aiWriteOk = agentAvailable && !isViewer
  // The template library (AB#269) opens in this same workspace, but it is not a bid: it has no
  // settings/members, no knowledge sources and no workflow. Those views fall back to its overview.
  const { isLibrary } = useTemplateLibrary(bid)
  const bidOnlyView = rawView === 'knowledge' || rawView === 'settings' || rawView === 'dashboard' || rawView === 'template'
  const view: View =
    isLibrary && bidOnlyView ? 'editor' : aiWriteOk ? rawView : rawView === 'dashboard' ? 'editor' : rawView
  useEffect(() => {
    if (((!aiWriteOk && rawView === 'dashboard') || (isLibrary && bidOnlyView)) && bid) navigate(bidPath(bid))
  }, [aiWriteOk, isLibrary, bidOnlyView, rawView, bid])
  // Right column: the ChatPanel (AI — editor/dashboard) or the non-AI SourceInspector (knowledge).
  // Deliberately `agentAvailable`, NOT `aiWriteOk`: a plain prompt with no part open is an ASK,
  // which is a read and a viewer's right (backend gates /v1/ask as `member`). An edit-intent
  // message from a viewer is still refused server-side at the revision.
  // The template view (AB#454) keeps the chat: with no part open, a prompt there is an ask.
  const chatColumn = agentAvailable && (view === 'editor' || view === 'dashboard' || view === 'template')
  // Whether the right column has visible content: the inspector (always, in knowledge) or the
  // chat (only when AI is on and not collapsed). When false the layout is 2-column (--chat-w: 0).
  const rightVisible = view === 'knowledge' || (chatColumn && chatOpen)
  // The "Open chat" affordance, when the column exists but is currently collapsed. On the bid
  // overview it rides along INSIDE the "Bid settings" / "New artefact" row (see BidHome's
  // `chatReopen` prop) — same `.home-cta` flex-wrap and height, so it can never mismatch or
  // overlap that row the way a fixed-position guess at its on-screen coordinates once did. Every
  // open part rides in the editor top bar instead, right of the "Live · N editing" pill (Canvas's
  // `chatReopen` prop, AB#338). Every other view has no equivalent row to join, so it falls back to
  // the floating `.chat-reopen` pill rendered below.
  const bidHomeShown = view === 'editor' && !room
  const partShown = view === 'editor' && !!room
  // Only an open part has focus mode; leaving it (another part, the bid home, a view) ends it.
  const focusMode = focusRequested && view === 'editor' && !!room
  useEffect(() => {
    setFocusRequested(false)
  }, [view, room])
  // Esc leaves focus mode — unless something else is already using that Esc: a handler that
  // claimed it (a slide's text edit), a form field, or an open dialog, menu or popover. The prose
  // body has no use for Esc, so it does leave from there.
  useEffect(() => {
    if (!focusMode) return
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      const target = e.target instanceof Element ? e.target : null
      // Esc inside a guided-tour window stops the tour, and only the tour.
      if (inTourWindow(target)) return
      if (target?.closest('input, textarea, select, [role="dialog"], [role="menu"], [role="listbox"], dialog')) return
      // A guided tour's own windows (non-modal) are not "something else open": Esc still leaves.
      const open = document.querySelectorAll('dialog[open], [role="dialog"], [aria-modal="true"], [role="menu"], [aria-haspopup][aria-expanded="true"]')
      if ([...open].some((el) => !inTourWindow(el))) return
      setFocusRequested(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [focusMode])
  // What the guided-tour probes read (workspace.focusMode / workspace.chatOpen): booleans only.
  const tourWorkspace = useMemo<WorkspaceTourState>(
    () => ({ focusMode, chatOpen: chatColumn && chatOpen }),
    [focusMode, chatColumn, chatOpen],
  )
  useTourState(WORKSPACE_STATE, tourWorkspace)
  const chatReopenButton = chatColumn && !chatOpen ? (
    <button className="home-cta" aria-label={t('open_chat')} title={t('open_chat')} onClick={() => setChatOpen(true)}>
      <span aria-hidden="true">◆</span> {t('open_chat')}
    </button>
  ) : null
  // The same control at the editor top bar's scale (a `.doc-top-act`), so the bar does not grow.
  const chatReopenTopAct = chatColumn && !chatOpen ? (
    <button className="doc-top-act chat-reopen-act" aria-label={t('open_chat')} title={t('open_chat')} onClick={() => setChatOpen(true)}>
      <span aria-hidden="true">◆</span>{' '}
      {/* Hidden visually at narrow widths (icon-only), never from the accessible name. */}
      <span className="chat-reopen-label">{t('open_chat')}</span>
    </button>
  ) : null

  const queryClient = useQueryClient()
  const bids = useQuery({ queryKey: ['bids'], queryFn: api.bids })
  const bidList = bids.data?.bids ?? []
  // The bid's LABEL ("Client - Bid name", lib/api.ts#bidDisplayLabel), not its raw name: this one
  // value feeds the breadcrumb, the tab title, BidHome's heading and BidSettingsPage's aria-label.
  const bidRecord = bidList.find((b) => b.id === bid)
  // The library is in no bid list; it is named "Template library" wherever a bid name would show.
  const bidName = isLibrary ? t('template_library') : bidRecord ? bidDisplayLabel(bidRecord) : (bid ?? '—')

  // Real headcount for the "N connected" pill: the people connected to the bid — the SAME list
  // the avatar roster right next to it renders (lib/connected.ts), so the pill always counts the
  // avatars (+N) beside it, whether a part is open or not. The signed-in member is merged into that
  // list, which is what keeps the count at 1 or more. Per-part counts stay in the left nav. Named
  // "connected", not "Live" or "editing": this is a 5s REST poll of who is connected, with no
  // socket-health check behind it — see Canvas's own `health`-gated label for the one indicator
  // entitled to say "Live".
  const connectedCount = useConnected(bid).length

  // Breadcrumb: ⌂ › bid › {Dashboard | artefact › section | Workflow | Knowledge sources}.
  // The last two crumbs come from the outline (same query as the left nav — read above), so the
  // trail shows display titles instead of the raw "bid/artefact/part" room key.
  const openPartCrumbs = room ? roomTitles(artefacts, room) : null
  // Which editor the Canvas mounts. Read from the OUTLINE rather than from the room's own ydoc:
  // the CRDT's `meta` discriminator is authoritative but only arrives after the first sync, so
  // branching on it would flash the wrong editor on every open. Unknown room = prose, today's
  // behaviour and what an older server's outline implies.
  const openPartKind = room ? partKindOf(artefacts, room) : 'markdown'
  const openArtefactType = room ? artefactTypeOfRoom(artefacts, room) : 'prose'
  // A read-only PART (AB#209) opens exactly as it does for a viewer, for everyone; its banner names
  // the part it was restored from — by title while that part is still in the outline.
  const openPartItem = room ? partOf(artefacts, room) : null
  const partLock = openPartItem?.readOnly
    ? {
        ...(openPartItem.restoredFrom
          ? {
              restoredFrom: {
                part: partOf(artefacts, openPartItem.restoredFrom.room)?.title ?? openPartItem.restoredFrom.room,
                at: openPartItem.restoredFrom.at,
              },
            }
          : {}),
      }
    : undefined
  // Grid parts are in beta (lib/beta.ts). Resolved here because Canvas has no QueryClient.
  const { isBeta } = useBetaTester()

  // keep the document language attribute in sync (a11y)
  useEffect(() => {
    document.documentElement.lang = lang
  }, [lang])

  // Same pattern, for the colour scheme + text-size preference (theme.tsx / TopBar's Appearance
  // menu). The inline script in index.html already set these attributes before first paint (FOUC
  // guard) — this effect only needs to handle CHANGES after mount (a member picking a new scheme,
  // or their OS-level preference flipping under `system`).
  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme
  }, [resolvedTheme])
  useEffect(() => {
    document.documentElement.dataset.textScale = textScale
  }, [textScale])

  // Name the browser tab after the open bid — members keep several bids open side by side, and
  // an identical title on every tab makes them indistinguishable. The bid name alone is the tab
  // text (no product suffix, which would only eat the narrow tab width). Outside a bid (landing
  // page, release notes) the tab shows the product title.
  useEffect(() => {
    document.title = bid ? bidName : defaultTitle
  }, [bid, bidName])

  // A niobe:// deep link opened in Niobe Desktop: follow it with the router, so the session and the
  // CRDT rooms survive. Mounted here, past sign-in, which is what tells the shell the page is ready.
  useEffect(() => onDesktopNavigate(navigate), [])

  // Navigation = URL changes (each gives a dedicated, shareable address).
  const enterBid = (id: string): void => navigate(bidPath(id))
  const openPart = (name: string): void => navigate(roomPath(name))
  // A part the editor just created ("Restore as part"): refresh the outline first, so the new
  // part's kind and read-only flag are known when it opens instead of after the next poll.
  const openCreatedPart = (name: string): void => {
    void queryClient.invalidateQueries({ queryKey: ['docs', bid] }).then(() => openPart(name))
  }
  const goBidHome = (): void => {
    if (bid) navigate(bidPath(bid))
  }
  const goBidSettings = (): void => {
    if (bid) navigate(settingsPath(bid))
  }
  const onNavigate = (v: View): void => {
    if (v === 'home') navigate(homePath)
    else if (v === 'knowledge' && bid) navigate(knowledgePath(bid))
    else if (v === 'dashboard' && aiWriteOk && bid) navigate(dashboardPath(bid))
    else if (v === 'editor' && (view === 'knowledge' || view === 'dashboard' || view === 'template') && bid) navigate(bidPath(bid))
    // v === 'editor' while already editing: no-op — keep the open part.
  }
  // The Help menu's "Guided tours" entry opens the Guided tours window (App above).
  const openGuidedTours = onOpenGuidedTours

  if (view === 'home') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <HomePage bids={bidList} loading={bids.isLoading} onOpenBid={enterBid} />
        <Footer />
      </div>
    )
  }

  if (view === 'releases') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <ReleaseNotes />
        <Footer />
      </div>
    )
  }

  if (view === 'account-settings') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <AccountSettingsPage initialSection={settingsSection} />
        <Footer />
      </div>
    )
  }

  if (view === 'corporate-templates') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <CorporateTemplatesPage />
        <Footer />
      </div>
    )
  }

  if (view === 'version-retention') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <VersionRetentionPage />
        <Footer />
      </div>
    )
  }

  if (view === 'feedback-triage') {
    return (
      <div className="app app-home">
        <TopBar onOpenGuidedTours={openGuidedTours} />
        <FeedbackTriagePage />
        <Footer />
      </div>
    )
  }

  return (
    <div className={'app' + (focusMode ? ' focus' : '')}>
      {/* Focus mode (AB#282) leaves the workspace alone on screen: no top bar, breadcrumb bar or
          footer. Nothing the editor needs lives in them; the contract button and Esc lead out. */}
      {!focusMode && <TopBar onOpenGuidedTours={openGuidedTours} />}

      {/* breadcrumb bar — it carries the nav toggle, because this shell is the only one that
          renders a left nav to collapse. The home and releases shells above have none. */}
      {!focusMode && (
      <div className="subbar">
        <button className="navtoggle" aria-label="Toggle navigation" title="Collapse / expand navigation" onClick={() => setNavHidden((v) => !v)}>☰</button>
        <nav className="crumb" aria-label="Breadcrumb">
          <button className="home crumb-link" onClick={() => navigate(homePath)} title={t('all_bids')} aria-label={t('all_bids')}><span aria-hidden="true">⌂</span></button>
          <span className="sep">›</span>
          {/* `bid` is weight only, not a <b>/<strong>: the emphasis is visual (which bid am I in?),
              and real strong-importance markup inside a link reads as noise to a screen reader. */}
          <button className="crumb-link bid" onClick={goBidHome}>{bidName}</button><span className="sep">›</span>
          {openPartCrumbs ? (
            <>
              {/* An artefact has no view of its own, so this crumb names the container only. */}
              <span className="seg">{openPartCrumbs.artefact}</span><span className="sep">›</span>
              <b aria-current="page">{openPartCrumbs.section}</b>
            </>
          ) : (
            <b aria-current="page">
              {view === 'dashboard'
                ? t('nav_dashboard')
                : view === 'settings'
                  ? t('bid_settings_title')
                  : view === 'template'
                    ? t('tplv_crumb')
                    : view === 'editor'
                      ? t('crumb_overview')
                      : t('crumb_kb')}
            </b>
          )}
        </nav>
        {bid && <BidUsage bid={bid} variant="compact" />}
        {view === 'editor' || view === 'template' ? (
          <span className="pill" role="status" aria-live="polite">
            <span className="dot" /> {connectedCount <= 1 ? t('connected_1') : t('connected_n').replace('{n}', () => String(connectedCount))}
          </span>
        ) : (
          <span className="pill" role="status" aria-live="polite"><span className="dot" /> {t('kb_indexed')}</span>
        )}
        {/* The connected/editing-user roster, at the right of the "Live" pill it echoes — moved
            out of the top bar, which carries the product identity and the account menu instead. */}
        <ConnectedRoster bid={bid} />
      </div>
      )}

      <div
        className={'workspace' + (navHidden || focusMode ? ' navhide' : '') + (focusMode ? ' focus' : '') + (rightVisible ? '' : ' chathide') + (resizing ? ' resizing' : '')}
        style={{ '--nav-w': `${navWidth}px`, '--chat-w': rightVisible ? `${chatWidth}px` : '0px' } as CSSProperties}
      >
        {!navHidden && !focusMode && (
          <div
            className="nav-resizer"
            style={{ left: 'var(--nav-w)' }}
            onMouseDown={startNavResize}
            role="separator"
            aria-orientation="vertical"
            aria-label={t('resize_panel')}
            title={t('resize_panel')}
          />
        )}
        <LeftNav
          view={view}
          onNavigate={onNavigate}
          onOverview={goBidHome}
          bids={bidList}
          activeBid={bid}
          onSelectBid={enterBid}
          openRoom={room}
          onOpenPart={openPart}
          onRoomsDeleted={(rooms) => {
            if (room && rooms.includes(room)) goBidHome()
          }}
        />
        {view === 'editor' ? (
          room ? (
            <Canvas room={room} kind={openPartKind} artefactType={openArtefactType} beta={isBeta} admin={isAdmin} wsUrl={session.wsUrl} user={session.user} readOnly={isViewer || partLock !== undefined} partLock={partLock} viewer={isViewer} partTitle={openPartItem?.title} artefactTitle={openPartCrumbs?.artefact} onOpenPart={openCreatedPart} focusMode={focusMode} onToggleFocus={() => setFocusRequested((v) => !v)} chatReopen={chatReopenTopAct} />
          ) : (
            <BidHome
              bid={bid}
              bidName={bidName}
              onOpenPart={openPart}
              onOpenWorkflow={() => bid && navigate(dashboardPath(bid))}
              onOpenSettings={goBidSettings}
              chatReopen={chatReopenButton}
            />
          )
        ) : view === 'dashboard' ? (
          <DashboardView bid={bid} onOpenRoom={openPart} />
        ) : view === 'settings' ? (
          <BidSettingsPage bid={bid} bidName={bidName} />
        ) : view === 'template' && bid && templateId ? (
          <Suspense fallback={<div className="canvasWrap" aria-busy="true" />}>
            <TemplateView key={`${bid}/${templateId}`} bid={bid} templateId={templateId} wsUrl={session.wsUrl} user={session.user} />
          </Suspense>
        ) : (
          <KnowledgeView bid={bid} />
        )}
        {chatColumn ? (
          <ChatPanel bid={bid} room={room} partKind={openPartKind} onCollapse={() => setChatOpen(false)} />
        ) : view === 'knowledge' ? (
          // SourceInspector is the knowledge-view side panel — NOT part of the AI surface, so it
          // stays even when AI is off. In editor view with AI off the right column is empty.
          <SourceInspector bid={bid} />
        ) : null}
        {rightVisible && (
          <div
            className="chat-resizer"
            style={{ right: 'var(--chat-w)' }}
            onMouseDown={startChatResize}
            role="separator"
            aria-orientation="vertical"
            aria-label={t('resize_panel')}
            title={t('resize_panel')}
          />
        )}
      </div>

      {/* Fallback for every view but the bid overview and an open part: those have no "Bid
          settings" / "New artefact"-style row or editor top bar to ride along in, so this one
          still floats. */}
      {chatReopenButton && !bidHomeShown && !partShown && (
        <button className="chat-reopen" aria-label={t('open_chat')} title={t('open_chat')} onClick={() => setChatOpen(true)}>
          ◆ <span>{t('open_chat')}</span>
        </button>
      )}

      {!focusMode && <Footer />}
    </div>
  )
}
